import {PurchaseHistoryStatuses, PurchaseOrderStatuses} from '../constants';

/**
 * Recomputes money totals from line items (shell).
 * @param {Array<{orderQty?: number, cost?: number}>} items
 * @param {{discount?: number, tax?: number, balance?: number, freight?: number}} [prev]
 */
export const recomputePurchaseTotals = (items, prev = {}) => {
    const subtotal = (items ?? []).reduce((sum, row) => {
        const qty = Number(row.orderQty) || 0;
        const cost = Number(row.cost) || 0;
        const disc = Number(row.discountPct) || 0;
        const extended = qty * cost * (1 - disc / 100);
        return sum + extended;
    }, 0);
    const discount = Number(prev.discount) || 0;
    const tax = Number(prev.tax) || 0;
    const freight = Number(prev.freight) || 0;
    const total = Math.max(0, subtotal - discount + tax + freight);
    return {
        subtotal,
        discount,
        tax,
        freight,
        total,
        balance: Number.isFinite(Number(prev.balance)) ? Number(prev.balance) : total,
    };
};

/**
 * Shared billing / shipping addresses for purchase shell documents.
 * @param {string} vendorName
 */
const buildAddresses = (vendorName) => ({
    billingAddress: {
        fullAddress: 'Zhendong Village, Hai\'an',
        city: "Rui'an City",
        province: 'ZH',
        postalCode: '',
        country: 'China',
        phone: '+8613736917108',
        name: vendorName || 'Vendor billing',
    },
    shippingAddress: {
        fullAddress: '15018 196C St',
        city: 'Pitt Meadows',
        province: 'BC',
        postalCode: 'V3Y1Z1',
        country: 'CA',
        phone: '',
        name: 'Main warehouse',
    },
});

/**
 * Builds a longer line list so print pagination can be verified (~25 rows).
 * @returns {Array<object>}
 */
const buildPrintLineItems = () => {
    const catalog = [
        {partNo: 'RADIN157A', vendorPartNo: 'SGEM-001', description: 'Radiator, International', orderQty: 4, cost: 260, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADIN170A', vendorPartNo: 'SGEM-003', description: 'Radiator, International', orderQty: 2, cost: 280, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADIN171A', vendorPartNo: 'SGBM-003', description: 'Radiator, International', orderQty: 1, cost: 168, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADMA102A', vendorPartNo: 'SGJF-148', description: 'Radiator, Mack', orderQty: 3, cost: 135.13, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADMA117AA', vendorPartNo: 'SGJF-221', description: 'Radiator, Mack', orderQty: 3, cost: 350, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADMA101A', vendorPartNo: 'SGJF-183', description: 'Radiator, Mack', orderQty: 1, cost: 155.91, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADFR165A', vendorPartNo: 'SGJF-196', description: 'Radiator, Freightliner', orderQty: 1, cost: 200, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADIN153A', vendorPartNo: 'SGJF-180', description: 'Radiator, International', orderQty: 3, cost: 252, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADFO117A', vendorPartNo: 'SGEF-063', description: 'Radiator, Ford', orderQty: 3, cost: 145, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADPE120AB', vendorPartNo: 'SGUA-001F', description: 'Radiator, Peterbilt', orderQty: 2, cost: 350, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADIN137A', vendorPartNo: 'SGJF-177', description: 'Radiator, International', orderQty: 2, cost: 168, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADIN162A', vendorPartNo: 'SGJF-182', description: 'Radiator, International', orderQty: 2, cost: 250, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADKE116AB', vendorPartNo: 'RADKE116AB', description: 'Radiator, Kenworth', orderQty: 1, cost: 409.82, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADKE116A', vendorPartNo: 'SGEF-053', description: 'Radiator, Kenworth', orderQty: 2, cost: 210.97, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADIN154A', vendorPartNo: 'SGJF-181', description: 'Radiator, International', orderQty: 3, cost: 213.46, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADWE101A', vendorPartNo: 'SGJF-186', description: 'Radiator, Westernstar', orderQty: 3, cost: 214.37, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADFR164A', vendorPartNo: 'SGEF-061', description: 'Radiator, Freightliner', orderQty: 2, cost: 280, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADFR120A', vendorPartNo: 'SGJF-089', description: 'Radiator, Freightliner', orderQty: 5, cost: 257.94, discountPct: 0, unit: 'EA', serials: ['SN-RAD-001']},
        {partNo: 'RADCH126A', vendorPartNo: 'RADCH126A', description: 'Radiator, GMC', orderQty: 1, cost: 0, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADMA117AAB', vendorPartNo: 'RADMA117AAB', description: 'Radiator, Mack', orderQty: 1, cost: 539.89, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADFR125AB2', vendorPartNo: 'SGJF-090F', description: 'Radiator, Freightliner', orderQty: 2, cost: 325, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADPE111AB', vendorPartNo: 'SGEF-022F', description: 'Radiator, Peterbilt, Kenworth', orderQty: 2, cost: 346.5, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADPE112AB', vendorPartNo: 'SGEF-019F', description: 'Radiator, Peterbilt, Kenworth', orderQty: 2, cost: 437.13, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'RADFR123AB2', vendorPartNo: 'SGJF-085F', description: 'Radiator, Freightliner', orderQty: 2, cost: 310, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'CACMT101', vendorPartNo: 'CACMT101', description: 'Charge air cooler, Mitsubishi', orderQty: 1, cost: 647.37, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'CACWE104', vendorPartNo: 'ZGCc-043', description: 'Charge Air Cooler, Westernstar', orderQty: 2, cost: 235, discountPct: 0, unit: 'EA', serials: []},
        {partNo: 'CACFR107', vendorPartNo: 'ZGBC-015', description: 'Charge Air Cooler, Freightliner', orderQty: 1, cost: 250, discountPct: 0, unit: 'EA', serials: []},
    ];
    return catalog.map((row, index) => ({
        ...row,
        id: `line-${index + 1}`,
        comments: '',
        extendedCost: Number(row.orderQty) * Number(row.cost) * (1 - (Number(row.discountPct) || 0) / 100),
    }));
};

/**
 * Shell purchase order document.
 * // TODO: live GetPurchaseOrder API
 * @param {{orderId: string|number, vendorId: string|number, vendorName?: string, vendorCode?: string}} params
 */
export const buildMockPurchaseOrder = ({orderId, vendorId, vendorName, vendorCode}) => {
    const items = buildPrintLineItems();
    const money = recomputePurchaseTotals(items, {discount: 0, tax: 0, freight: 0, balance: 0});
    money.balance = money.total;
    const resolvedName = vendorName || vendorCode || 'Zhejiang A-Star Cooling Co Ltd.';
    return {
        id: orderId,
        orderNo: `0000714765`,
        documentNo: '0000714765',
        vendorId,
        vendorName: resolvedName,
        vendorCode: vendorCode || '--',
        vendorNo: '0000020236',
        vendorPO: 'VPO-2201',
        reference: '-',
        buyer: '',
        shipVia: '',
        paymentTerms: 'Net 60',
        incoterms: '',
        date: new Date().toISOString(),
        requiredDate: new Date(Date.now() + 1000 * 60 * 60 * 24 * 7).toISOString(),
        status: PurchaseOrderStatuses.open,
        currency: 'US Dollars',
        warehouse: {id: 1, code: 'MAIN', name: 'Main warehouse'},
        ...money,
        ...buildAddresses(resolvedName),
        items,
        logs: [
            {
                id: 'log-1',
                loggedAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
                user: 'j.smith',
                action: 'Created',
                details: 'Purchase order created.',
            },
        ],
    };
};

/**
 * Shell purchase-history document.
 * // TODO: live GetPurchaseHistory API
 * @param {{historyId: string|number, vendorId: string|number, vendorName?: string, vendorCode?: string}} params
 */
export const buildMockPurchaseHistory = ({historyId, vendorId, vendorName, vendorCode}) => {
    const items = buildPrintLineItems().map((row, index) => {
        if (index === 0) {
            return {...row, serials: ['SN-AF-001', 'SN-AF-002'], comments: 'Priority restock'};
        }
        if (index === 2) {
            return {...row, serials: ['HS-LOT-A'], comments: 'QC hold on lot'};
        }
        return row;
    });
    const money = recomputePurchaseTotals(items, {discount: 0, tax: 0, freight: 0, balance: 0});
    const resolvedName = vendorName || vendorCode || 'Zhejiang A-Star Cooling Co Ltd.';
    return {
        id: historyId,
        historyNo: historyId || 'PH-10045',
        documentNo: historyId || 'PH-10045',
        orderNo: historyId || 'PH-10045',
        vendorId,
        vendorName: resolvedName,
        vendorCode: vendorCode || '--',
        vendorNo: '0000020236',
        vendorPO: 'VPO-8891',
        reference: 'REF-4412',
        buyer: '',
        shipVia: '',
        paymentTerms: 'Net 60',
        incoterms: '',
        date: new Date().toISOString(),
        receivedDate: new Date().toISOString(),
        requiredDate: new Date().toISOString(),
        status: PurchaseHistoryStatuses.partial,
        currency: 'US Dollars',
        warehouse: 'MAIN',
        ...money,
        ...buildAddresses(resolvedName),
        items,
        logs: [
            {
                id: 'log-1',
                loggedAt: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
                user: 'j.smith',
                action: 'Created',
                details: 'Purchase history created from receipts batch #991.',
            },
            {
                id: 'log-2',
                loggedAt: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
                user: 'a.lee',
                action: 'Status changed',
                details: `Status set to ${PurchaseHistoryStatuses.partial}.`,
            },
            {
                id: 'log-3',
                loggedAt: new Date(Date.now() - 1000 * 60 * 25).toISOString(),
                user: 'j.smith',
                action: 'Serials updated',
                details: 'Serial numbers recorded for AF-100.',
            },
        ],
    };
};
